import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Sheet,
  Avatar,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Select,
  Option,
  Input,
  Button,
} from "@mui/joy";
import { format, differenceInMinutes, parse } from "date-fns";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { PDFDownloadLink } from "@react-pdf/renderer";

import { supabase } from "../../supabaseClient";
import { fetchProfilePicture, getInitials, formatStudentName, formatDuration, getLocalDateString, formatISOTimestamp } from "../../utils";
import AttendancePDF from "../pdf/AttendancePDF";

import { CheckinCheckoutRecordView, ActionType } from "../../types/CheckinCheckoutRecord";
import { RelationshipType, RelationshipTypeLabels } from "../../types/ParentGuardian";
import SignatureModal from "../modals/SignatureModal";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../Pagination";

interface ClassAttendanceTableProps {
  classId: string | undefined;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  preferred_first_name?: string | null;
  profile_picture: string | null;
}

interface ParentGuardian {
  id: string;
  first_name: string;
  last_name: string;
  relationship: RelationshipType;
}

interface CheckInOutRecord {
  created_at: string;
  student: Student;
  parent_guardian: ParentGuardian;
  signature: string;
}

interface TableDailyAttendanceRecord {
  date: string;
  studentId: string;
  checkin: CheckinCheckoutRecordView | null;
  checkout: CheckinCheckoutRecordView | null;
}

interface PDFDailyAttendanceRecord {
  checkin: CheckInOutRecord | null;
  checkout: CheckInOutRecord | null;
}

interface TableDailyStats {
  [date: string]: {
    checkedIn: number;
    totalEnrolled: number;
    records: TableDailyAttendanceRecord[];
  };
}

interface PDFDailyStats {
  [date: string]: {
    checkedIn: number;
    totalEnrolled: number;
    records: PDFDailyAttendanceRecord[];
  };
}

interface EnrolledStudent {
  id: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
  profile_picture_id: string | null;
}

interface TeacherInfo {
  id: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
}

interface ClassDetails {
  name: string;
  teacher: TeacherInfo;
}



const transformRecordForPDF = (record: CheckinCheckoutRecordView): CheckInOutRecord => {
  return {
    created_at: record.created_at,
    student: {
      id: record.student.id,
      first_name: record.student.first_name,
      last_name: record.student.last_name,
      preferred_first_name: record.student.preferred_first_name,
      profile_picture: record.student.profile_picture || null,
    },
    parent_guardian: {
      id: record.parent_guardian.id,
      first_name: record.parent_guardian.first_name,
      last_name: record.parent_guardian.last_name,
      relationship: record.parent_guardian.relationship as RelationshipType,
    },
    signature: record.signature,
  };
};

const transformRecordsForPDF = (records: TableDailyStats): PDFDailyStats => {
  const transformed: PDFDailyStats = {};
  
  Object.entries(records).forEach(([date, data]) => {
    transformed[date] = {
      checkedIn: data.checkedIn,
      totalEnrolled: data.totalEnrolled,
      records: data.records.map(record => ({
        checkin: record.checkin ? transformRecordForPDF(record.checkin) : null,
        checkout: record.checkout ? transformRecordForPDF(record.checkout) : null,
      })),
    };
  });
  
  return transformed;
};

const ClassAttendanceTable: React.FC<ClassAttendanceTableProps> = ({ classId }) => {
  const navigate = useNavigate();
  const [attendanceRecords, setAttendanceRecords] = useState<TableDailyStats>({});
  const [loading, setLoading] = useState(true);
  const [enrolledStudents, setEnrolledStudents] = useState<EnrolledStudent[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [signatureModal, setSignatureModal] = useState<{
    open: boolean;
    signatureData: string;
    action: ActionType;
    timestamp: string;
    parentName: string;
  }>({
    open: false,
    signatureData: "",
    action: "checkin",
    timestamp: "",
    parentName: "",
  });
  const [classDetails, setClassDetails] = useState<ClassDetails | null>(null);

  // Flatten records for pagination while preserving date structure
  const flattenedRecords = React.useMemo(() => {
    const records: Array<{ date: string; record: TableDailyAttendanceRecord; dailyStats: { checkedIn: number; totalEnrolled: number } }> = [];
    
    Object.entries(attendanceRecords).forEach(([date, dailyStats]) => {
      dailyStats.records.forEach(record => {
        records.push({ date, record, dailyStats });
      });
    });
    
    return records;
  }, [attendanceRecords]);

  // Use pagination hook
  const {
    paginatedData: paginatedRecords,
    currentPage,
    totalPages,
    totalItems,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
  } = usePagination({
    data: flattenedRecords,
    searchTerm: "", // No search for attendance table
    searchFields: [],
  });

  const fetchAttendance = useCallback(async () => {
    if (!classId) return;

    try {
      // First, get the total number of enrolled students for each day
      const { data: enrollmentData, error: enrollmentError } = await supabase
        .from("classes_students")
        .select("student_id")
        .eq("class_id", classId);

      if (enrollmentError) throw enrollmentError;

      const totalEnrolled = enrollmentData.length;

      // Then get the attendance records
      const { data, error } = await supabase
        .from("checkin_checkout_records_view")
        .select("*")
        .eq("class_id", classId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Process records to include profile pictures
      const processedRecords = await Promise.all(
        data.map(async (record) => {
          const profilePicture = await fetchProfilePicture("students", record.student_profile_picture_id);
          return {
            ...record,
            student_profile_picture: profilePicture,
          };
        })
      );

      // Group records by date and student
      const groupedRecords = processedRecords.reduce((acc, record) => {
        const date = getLocalDateString(record.created_at);
        const studentId = record.student_id;

        if (!acc[date]) {
          acc[date] = {
            checkedIn: 0,
            totalEnrolled,
            records: [],
          };
        }

        let existingRecord = acc[date].records.find((r: TableDailyAttendanceRecord) => r.studentId === studentId);
        
        if (!existingRecord) {
          existingRecord = {
            date,
            studentId,
            checkin: null,
            checkout: null,
          };
          acc[date].records.push(existingRecord);
        }

        const recordData: CheckinCheckoutRecordView = {
          id: record.id,
          action: record.action,
          signature: record.signature,
          created_at: record.created_at,
          class: {
            id: record.class_id,
            schedule: record.schedule,
            start_month: record.start_month,
            start_year: record.start_year,
            end_month: record.end_month,
            end_year: record.end_year,
          },
          student: {
            id: record.student_id,
            first_name: record.student_first_name,
            last_name: record.student_last_name,
            preferred_first_name: record.student_preferred_first_name,
            profile_picture: record.student_profile_picture,
            date_of_birth: record.student_date_of_birth,
          },
          parent_guardian: {
            id: record.parent_guardian_id,
            first_name: record.parent_guardian_first_name,
            last_name: record.parent_guardian_last_name,
            relationship: record.parent_guardian_relationship,
          },
          teachers: {
            primary: record.primary_teacher_first_name ? {
              first_name: record.primary_teacher_first_name,
              last_name: record.primary_teacher_last_name,
            } : undefined,
            secondary: record.secondary_teacher_first_name ? {
              first_name: record.secondary_teacher_first_name,
              last_name: record.secondary_teacher_last_name,
            } : undefined,
          },
        };

        if (record.action === "checkin") {
          existingRecord.checkin = recordData;
        } else {
          existingRecord.checkout = recordData;
        }

        return acc;
      }, {} as TableDailyStats);

      // Count unique students who checked in for each day
      Object.keys(groupedRecords).forEach(date => {
        const uniqueCheckedInStudents = new Set(
          groupedRecords[date].records
            .filter((record: TableDailyAttendanceRecord) => record.checkin !== null)
            .map((record: TableDailyAttendanceRecord) => record.studentId)
        );
        groupedRecords[date].checkedIn = uniqueCheckedInStudents.size;
      });

      // Sort records within each day
      Object.keys(groupedRecords).forEach(date => {
        groupedRecords[date].records.sort((a: TableDailyAttendanceRecord, b: TableDailyAttendanceRecord) => {
          // Sort by check-in time first
          const aTime = a.checkin?.created_at;
          const bTime = b.checkin?.created_at;
          
          // If both have check-in times, sort by time
          if (aTime && bTime) {
            return new Date(aTime).getTime() - new Date(bTime).getTime();
          }
          
          // If only one has a check-in time, put the one with check-in first
          if (aTime) return -1;
          if (bTime) return 1;
          
          // If neither has check-in, sort by student name as fallback
          const aStudent = (a.checkin || a.checkout)?.student;
          const bStudent = (b.checkin || b.checkout)?.student;
          if (!aStudent || !bStudent) return 0;
          return formatStudentName(aStudent).localeCompare(formatStudentName(bStudent));
        });
      });

      setAttendanceRecords(groupedRecords);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  }, [classId]);

  const fetchEnrolledStudents = useCallback(async () => {
    if (!classId) return;
    
    try {
      const { data, error } = await supabase
        .from("classes_students")
        .select(`
          student:students (
            id,
            first_name,
            last_name,
            preferred_first_name,
            profile_picture_id
          )
        `)
        .eq("class_id", classId);

      if (error) throw error;

      // Process the nested student data and add profile pictures
      const students = await Promise.all(
        data.map(async (item: any) => {
          const student = item.student as EnrolledStudent;
          return {
            ...student,
            profile_picture: await fetchProfilePicture("students", student.profile_picture_id)
          } as EnrolledStudent;
        })
      );

      setEnrolledStudents(students);
    } catch (error) {
      console.error("Error fetching enrolled students:", error);
    }
  }, [classId]);

  const fetchClassDetails = useCallback(async () => {
    if (!classId) return;

    try {
      const { data, error } = await supabase
        .from("classes")
        .select(`
          *,
          primary_teacher:teachers!classes_primary_teacher_id_fkey(
            id,
            first_name,
            last_name,
            profile_picture_id
          )
        `)
        .eq("id", classId)
        .single();

      if (error) throw error;

      const teacherProfilePicture = await fetchProfilePicture("teachers", data.primary_teacher.profile_picture_id);

      setClassDetails({
        name: `${data.start_month}/${data.start_year} - ${data.end_month}/${data.end_year}`,
        teacher: {
          id: data.primary_teacher.id,
          first_name: data.primary_teacher.first_name,
          last_name: data.primary_teacher.last_name,
          profile_picture: teacherProfilePicture || null,
        },
      });
    } catch (error) {
      console.error("Error fetching class details:", error);
    }
  }, [classId]);

  useEffect(() => {
    fetchAttendance();
    fetchEnrolledStudents();
    fetchClassDetails();
  }, [fetchAttendance, fetchEnrolledStudents, fetchClassDetails]);

  const handleViewSignature = (record: CheckinCheckoutRecordView) => {
    setSignatureModal({
      open: true,
      signatureData: record.signature,
      action: record.action,
      timestamp: record.created_at,
      parentName: `${record.parent_guardian.first_name} ${record.parent_guardian.last_name}`,
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const sortedDates = Object.keys(attendanceRecords)
    .filter(date => {
      // Parse date string (YYYY-MM-DD) as local date using date-fns
      const currentDate = parse(date, 'yyyy-MM-dd', new Date());
      
      // If no dates selected, show all
      if (!startDate && !endDate) return true;
      
      // If only start date, show everything from that date onward
      if (startDate && !endDate) {
        return currentDate >= startDate;
      }
      
      // If only end date, show everything up to that date
      if (!startDate && endDate) {
        return currentDate <= endDate;
      }
      
      // If both dates, show everything in between (inclusive)
      return startDate && endDate ? currentDate >= startDate && currentDate <= endDate : true;
    })
    .sort((a, b) => {
      // Sort by parsing dates as local dates using date-fns
      const dateA = parse(a, 'yyyy-MM-dd', new Date());
      const dateB = parse(b, 'yyyy-MM-dd', new Date());
      return dateA.getTime() - dateB.getTime();
    });

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Input
              type="date"
              placeholder="Start date"
              value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
              onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
              slotProps={{
                input: {
                  min: "2020-01-01",
                  max: "2025-12-31"
                }
              }}
            />
            <Input
              type="date"
              placeholder="End date"
              value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
              onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
              slotProps={{
                input: {
                  min: "2020-01-01",
                  max: "2025-12-31"
                }
              }}
            />
          </Box>
          <Select
            placeholder="Filter by student"
            value={selectedStudent || ""}
            onChange={(_, value) => setSelectedStudent(value)}
            sx={{ minWidth: 240 }}
            slotProps={{
              listbox: {
                sx: { maxHeight: 300, overflow: "auto" }
              }
            }}
          >
            <Option value="">All Students</Option>
            {enrolledStudents.map((student) => (
              <Option
                key={student.id}
                value={student.id}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar
                    src={student.profile_picture || undefined}
                    sx={{ width: 24, height: 24 }}
                  >
                    {!student.profile_picture ? getInitials(student) : null}
                  </Avatar>
                  <Typography>{formatStudentName(student)}</Typography>
                </Box>
              </Option>
            ))}
          </Select>
        </Box>
        {classDetails && (() => {
          // Calculate total records count
          const totalRecords = sortedDates.reduce((total, date) => {
            const { records } = attendanceRecords[date];
            const filteredRecords = selectedStudent
              ? records.filter(record => (record.checkin || record.checkout)?.student.id === selectedStudent)
              : records;
            return total + filteredRecords.length;
          }, 0);

          // Generate filename
          const generateFileName = () => {
            let fileName = `attendance-${classDetails.name.replace(/\s+/g, '-')}`;
            
            // Add date range if provided
            if (startDate && endDate) {
              fileName += `-from-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}`;
            } else if (startDate) {
              fileName += `-from-${format(startDate, 'yyyy-MM-dd')}`;
            } else if (endDate) {
              fileName += `-to-${format(endDate, 'yyyy-MM-dd')}`;
            }
            
            // Add student filter if provided
            if (selectedStudent) {
              fileName += `-student-${selectedStudent}`;
            }
            
            return `${fileName}.pdf`;
          };

          return totalRecords > 0 ? (
            <PDFDownloadLink
              document={
                <AttendancePDF
                  className={classDetails.name}
                  teacher={classDetails.teacher}
                  startDate={startDate}
                  endDate={endDate}
                  selectedStudent={selectedStudent}
                  attendanceRecords={transformRecordsForPDF(attendanceRecords)}
                  filteredDates={sortedDates}
                />
              }
              fileName={generateFileName()}
            >
              {({ loading }) => (
                <Button
                  variant="outlined"
                  color="neutral"
                  startDecorator={<FileDownloadIcon />}
                  loading={loading}
                >
                  Download PDF
                </Button>
              )}
            </PDFDownloadLink>
          ) : (
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<FileDownloadIcon />}
              disabled
              title="No attendance records to download"
            >
              Download PDF
            </Button>
          );
        })()}
      </Box>
      <Sheet
        variant="outlined"
        sx={{
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        {paginatedRecords.length > 0 ? (
          <Table
            aria-label="attendance table"
            stickyHeader
            hoverRow
            sx={{
              "--TableCell-headBackground": "var(--joy-palette-background-level1)",
              "--Table-headerUnderlineThickness": "1px",
              "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
              "--TableCell-paddingY": "4px",
              "--TableCell-paddingX": "8px",
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 200, padding: "12px 6px" }}>Student</th>
                <th style={{ width: 200, padding: "12px 6px" }}>Check In</th>
                <th style={{ width: 200, padding: "12px 6px" }}>Check Out</th>
                <th style={{ width: 120, padding: "12px 6px" }}>In Class Time</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Group paginated records by date for display
                const groupedByDate = paginatedRecords.reduce((acc, { date, record, dailyStats }) => {
                  if (!acc[date]) {
                    acc[date] = {
                      dailyStats,
                      records: []
                    };
                  }
                  acc[date].records.push(record);
                  return acc;
                }, {} as Record<string, { dailyStats: { checkedIn: number; totalEnrolled: number }; records: TableDailyAttendanceRecord[] }>);

                return Object.entries(groupedByDate).map(([date, { dailyStats, records }]) => {
                  // Parse date string (YYYY-MM-DD) as local date using date-fns
                  const localDate = parse(date, 'yyyy-MM-dd', new Date());
                  // Filter records based on selected student
                  const filteredRecords = selectedStudent
                    ? records.filter(record => (record.checkin || record.checkout)?.student.id === selectedStudent)
                    : records;

                  // Skip date group if no records after filtering
                  if (filteredRecords.length === 0) return null;

                  return (
                    <React.Fragment key={date}>
                      <tr>
                        <td colSpan={4} style={{ padding: 0 }}>
                          <Box sx={{ py: 1, px: 1, bgcolor: "background.level1", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography level="title-sm">
                              {format(localDate, "EEEE, MMMM d, yyyy")}
                            </Typography>
                            <Typography level="body-sm">
                              {dailyStats.checkedIn} of {dailyStats.totalEnrolled} students checked in
                            </Typography>
                          </Box>
                        </td>
                      </tr>
                      {filteredRecords.map(record => {
                        const student = (record.checkin || record.checkout)?.student;
                        if (!student) return null;

                        // Calculate in-class time
                        let inClassTime = null;
                        if (record.checkin && record.checkout) {
                          const minutes = differenceInMinutes(
                            new Date(record.checkout.created_at),
                            new Date(record.checkin.created_at)
                          );
                          inClassTime = formatDuration(minutes);
                        }

                        return (
                          <tr key={`${date}-${student.id}`}>
                            <td>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  src={student.profile_picture || undefined}
                                  sx={{ width: 32, height: 32 }}
                                >
                                  {!student.profile_picture ? getInitials(student) : null}
                                </Avatar>
                                <Typography
                                  level="body-sm"
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => navigate(`/students/${student.id}`)}
                                >
                                  {formatStudentName(student)}
                                </Typography>
                              </Box>
                            </td>
                            <td>
                              {record.checkin ? (
                                <Box>
                                  <Typography level="body-sm">
                                    {formatISOTimestamp(record.checkin.created_at)}
                                  </Typography>
                                  <Typography level="body-xs">
                                    {record.checkin.parent_guardian.first_name} {record.checkin.parent_guardian.last_name} (
                                    {RelationshipTypeLabels[record.checkin.parent_guardian.relationship as RelationshipType]})
                                    {" - "}
                                    <Typography
                                      component="span"
                                      level="body-xs"
                                      sx={{
                                        color: "primary.500",
                                        cursor: "pointer",
                                        "&:hover": { textDecoration: "underline" }
                                      }}
                                      onClick={() => handleViewSignature(record.checkin!)}
                                    >
                                      View Signature
                                    </Typography>
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography level="body-sm">—</Typography>
                              )}
                            </td>
                            <td>
                              {record.checkout ? (
                                <Box>
                                  <Typography level="body-sm">
                                    {formatISOTimestamp(record.checkout.created_at)}
                                  </Typography>
                                  <Typography level="body-xs">
                                    {record.checkout.parent_guardian.first_name} {record.checkout.parent_guardian.last_name} (
                                    {RelationshipTypeLabels[record.checkout.parent_guardian.relationship as RelationshipType]})
                                    {" - "}
                                    <Typography
                                      component="span"
                                      level="body-xs"
                                      sx={{
                                        color: "primary.500",
                                        cursor: "pointer",
                                        "&:hover": { textDecoration: "underline" }
                                      }}
                                      onClick={() => handleViewSignature(record.checkout!)}
                                    >
                                      View Signature
                                    </Typography>
                                  </Typography>
                                </Box>
                              ) : (
                                <Chip
                                  variant="soft"
                                  color="warning"
                                  startDecorator={<WarningRoundedIcon />}
                                >
                                  Never Checked Out
                                </Chip>
                              )}
                            </td>
                            <td>
                              <Typography level="body-sm">
                                {inClassTime || "—"}
                              </Typography>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                });
              })()}
            </tbody>
          </Table>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
            <Typography level="body-lg" color="neutral" sx={{ mb: 1 }}>
              No Attendance Records Found
            </Typography>
            <Typography level="body-sm" color="neutral">
              {selectedStudent 
                ? "No attendance records found for the selected student in the specified date range."
                : "No attendance records found for this class in the specified date range."
              }
            </Typography>
          </Box>
        )}
        {paginatedRecords.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            rowsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        )}
        <SignatureModal
          open={signatureModal.open}
          onClose={() => setSignatureModal(prev => ({ ...prev, open: false }))}
          signatureData={signatureModal.signatureData}
          action={signatureModal.action}
          timestamp={signatureModal.timestamp}
          parentName={signatureModal.parentName}
        />
      </Sheet>
    </>
  );
};

export default ClassAttendanceTable; 